import { ApiAuthenticationFailedException } from '../errors'
import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { hibachiAccountCategory } from './constants'

const BASE_URL = 'https://api.hibachi.xyz'

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
}): Promise<Record<string, string>> => {
  const { apiKey, createHeaders } = params

  // プロキシ用の request headers 作成（Clerk認証用）
  const proxyHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/json',
    // Hibachi API Key は専用ヘッダーで送信（プロキシで Authorization に変換）
    'X-Hibachi-Authorization': apiKey,
    ...proxyHeaders,
  }
}

export const createClient = ({
  apiKey,
  createHeaders,
  proxy,
}: {
  /** 認証用 API Key */
  apiKey: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const queryString = buildQueryString(query)
      const queryWithQuestionMark = queryString ? `?${queryString}` : ''
      const url = `${BASE_URL}${path}${queryWithQuestionMark}`

      const headers = await createAuthHeaders({
        apiKey,
        createHeaders,
      })

      return {
        url,
        headers,
        body: method === 'POST' ? JSON.stringify(body) : undefined,
      }
    },
    getDelayForRetry: ({ statusCode, errorMessage }) => {
      // 401 エラーは認証失敗
      if (statusCode === 401) {
        throw new ApiAuthenticationFailedException(hibachiAccountCategory)
      }
      // 403 エラーも認証/権限エラーとして扱う
      if (statusCode === 403) {
        if (
          errorMessage.includes('Invalid API key') ||
          errorMessage.includes('Unauthorized')
        ) {
          throw new ApiAuthenticationFailedException(hibachiAccountCategory)
        }
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    proxy,
  })
